import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe, MapPin } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function CreateAd() {
  const navigate = useNavigate();
  const { user, locationData } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    link_url: '',
    targeting_level: 'national',
    target_location: {},
    budget: 50
  });

  useEffect(() => {
    if (!user || !user.is_business) {
      navigate('/upgrade-business');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/business/ads`, formData, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      });

      toast.success('Advertisement created!');
      navigate('/business-dashboard');
    } catch (error) {
      console.error('Error creating ad:', error);
      toast.error('Failed to create ad');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-20">
            <Button
              onClick={() => navigate('/business-dashboard')}
              variant="ghost"
              className="mr-4 border-2 border-black rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl md:text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Create Advertisement</h1>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white border-4 border-black rounded-3xl p-8 md:p-10 shadow-brutal-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="title" className="text-sm font-bold text-slate-900">Ad Title *</Label>
              <Input
                id="title"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="e.g., Best Tennis Equipment in SF"
                className="mt-1 h-14 border-2 border-black rounded-xl font-medium"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-bold text-slate-900">Description *</Label>
              <Textarea
                id="description"
                required
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Describe your product or service..."
                rows={4}
                className="mt-1 border-2 border-black rounded-xl font-medium resize-none"
              />
            </div>

            <div>
              <Label htmlFor="image_url" className="text-sm font-bold text-slate-900">Image URL *</Label>
              <Input
                id="image_url"
                type="url"
                required
                value={formData.image_url}
                onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                placeholder="https://example.com/image.jpg"
                className="mt-1 h-14 border-2 border-black rounded-xl font-medium"
              />
              <p className="text-xs text-slate-500 mt-1">Use high-quality images (recommended: 1200x628px)</p>
            </div>

            <div>
              <Label htmlFor="link_url" className="text-sm font-bold text-slate-900">Link URL *</Label>
              <Input
                id="link_url"
                type="url"
                required
                value={formData.link_url}
                onChange={(e) => setFormData({...formData, link_url: e.target.value})}
                placeholder="https://yourbusiness.com"
                className="mt-1 h-14 border-2 border-black rounded-xl font-medium"
              />
            </div>

            <div>
              <Label className="text-sm font-bold text-slate-900 mb-3 block">Targeting Level *</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div
                  onClick={() => setFormData({...formData, targeting_level: 'national', target_location: {}})}
                  className={`border-4 border-black rounded-2xl p-6 cursor-pointer transition-all ${
                    formData.targeting_level === 'national' ? 'bg-indigo-100 shadow-brutal' : 'bg-white hover:shadow-brutal'
                  }`}
                >
                  <Globe className="w-8 h-8 mb-3 text-indigo-500" />
                  <h3 className="text-lg font-black text-slate-900 mb-1" style={{fontFamily: 'Outfit'}}>National</h3>
                  <p className="text-xs text-slate-600">Show to friends in this area</p>
                  <p className="text-xs font-bold text-indigo-600 mt-2">$0.50/click</p>
                </div>

                <div
                  onClick={() => setFormData({...formData, targeting_level: 'state', target_location: {state: locationData?.state || 'California'}})}
                  className={`border-4 border-black rounded-2xl p-6 cursor-pointer transition-all ${
                    formData.targeting_level === 'state' ? 'bg-pink-100 shadow-brutal' : 'bg-white hover:shadow-brutal'
                  }`}
                >
                  <MapPin className="w-8 h-8 mb-3 text-pink-500" />
                  <h3 className="text-lg font-black text-slate-900 mb-1" style={{fontFamily: 'Outfit'}}>State</h3>
                  <p className="text-xs text-slate-600">Target a specific state</p>
                  <p className="text-xs font-bold text-pink-600 mt-2">$0.35/click</p>
                </div>

                <div
                  onClick={() => setFormData({...formData, targeting_level: 'local', target_location: {state: locationData?.state || 'California', town: locationData?.town || 'San Francisco'}})}
                  className={`border-4 border-black rounded-2xl p-6 cursor-pointer transition-all ${
                    formData.targeting_level === 'local' ? 'bg-lime-100 shadow-brutal' : 'bg-white hover:shadow-brutal'
                  }`}
                >
                  <MapPin className="w-8 h-8 mb-3 text-lime-600" />
                  <h3 className="text-lg font-black text-slate-900 mb-1" style={{fontFamily: 'Outfit'}}>Local</h3>
                  <p className="text-xs text-slate-600">Target a specific town</p>
                  <p className="text-xs font-bold text-lime-700 mt-2">$0.25/click</p>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="budget" className="text-sm font-bold text-slate-900">Budget (USD) *</Label>
              <Input
                id="budget"
                type="number"
                required
                min="10"
                step="10"
                value={formData.budget}
                onChange={(e) => setFormData({...formData, budget: parseFloat(e.target.value)})}
                className="mt-1 h-14 border-2 border-black rounded-xl font-medium"
              />
              <p className="text-xs text-slate-500 mt-1">Minimum $10. Ad will pause when budget is reached.</p>
            </div>

            <div className="bg-cyan-100 border-2 border-black rounded-xl p-4">
              <p className="text-sm font-bold text-slate-900">💡 Pro Tip</p>
              <p className="text-xs text-slate-700 mt-1">Local ads cost less per click and have higher conversion rates!</p>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-14 text-lg font-bold bg-indigo-500 hover:bg-indigo-600 text-white rounded-full shadow-brutal border-2 border-black"
            >
              {loading ? 'Creating...' : `Create Ad ($${formData.budget} Budget)`}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}